"use client";

import {
    ACTIVATE_SURVEY,
    CLOSE_SURVEY,
    DELETE_SURVEY,
} from "@/graphql/mutations/surveyMutations";
import { SURVEYS_QUERY } from "@/graphql/queries/surveyQueries";
import { useMutation, useQuery } from "@apollo/client";
import { BarChart2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface Survey {
    id: string;
    title: string;
    status: string;
    targetType: string;
    isAnonymous: boolean;
    vpReward: number;
    responseCount: number;
    hasResponses: boolean;
    viewCount: number;
    completionRate: number;
    endDate: string | null;
    createdAt: string;
}

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
    draft: {
        label: "Draft",
        className:
            "bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300",
    },
    active: {
        label: "Active",
        className:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    },
    closed: {
        label: "Closed",
        className:
            "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    },
};

const TARGET_LABELS: Record<string, string> = {
    all: "All Users",
    specific_users: "Specific Users",
    gota: "GOTA Members",
    stream_team: "Stream Team",
    moderators: "Moderators",
    subscribers: "Subscribers",
};

export default function SurveysPage() {
    const navigate = useNavigate();
    const { data, loading, error, refetch } = useQuery(SURVEYS_QUERY, {
        fetchPolicy: "network-only",
    });
    const [deletingSurveyId, setDeletingSurveyId] = useState<string | null>(
        null,
    );

    const [activateSurvey] = useMutation(ACTIVATE_SURVEY, {
        onCompleted: () => refetch(),
    });
    const [closeSurvey] = useMutation(CLOSE_SURVEY, {
        onCompleted: () => refetch(),
    });
    const [deleteSurvey] = useMutation(DELETE_SURVEY, {
        onCompleted: () => {
            setDeletingSurveyId(null);
            refetch();
        },
    });

    const surveys: Survey[] = data?.surveys ?? [];

    if (loading) {
        return (
            <div className="flex min-h-[200px] items-center justify-center">
                <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                Error loading surveys: {error.message}
            </div>
        );
    }

    return (
        <div className="mx-auto mt-4 w-full px-4">
            {/* Page Header */}
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                            Surveys
                        </h1>
                        <p className="mt-2 text-slate-600 dark:text-slate-400">
                            Create and manage surveys for your community
                        </p>
                    </div>
                    <button
                        onClick={() => navigate("/longhouse/surveys/new")}
                        className="bg-brand hover:bg-brand/90 flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors"
                    >
                        <Plus className="h-5 w-5" />
                        Create Survey
                    </button>
                </div>
            </div>

            {surveys.length === 0 ? (
                <div className="rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <p className="text-slate-600 dark:text-slate-400">
                        No surveys yet. Create your first survey to get started.
                    </p>
                </div>
            ) : (
                <div className="rounded-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Title
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Target
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    VP
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Responses
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Views
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Completion
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {surveys.map((survey) => {
                                const badge =
                                    STATUS_BADGES[survey.status] ??
                                    STATUS_BADGES.draft;
                                return (
                                    <tr
                                        key={survey.id}
                                        className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                                    >
                                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                                            <span className="font-medium text-slate-900 dark:text-slate-100">
                                                {survey.title}
                                            </span>
                                            {survey.isAnonymous && (
                                                <span className="ml-2 text-xs text-slate-400 dark:text-slate-600">
                                                    (anonymous)
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                                            <span
                                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${badge.className}`}
                                            >
                                                {badge.label}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600 dark:text-slate-400">
                                            {TARGET_LABELS[survey.targetType] ??
                                                survey.targetType}
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-700 dark:text-slate-300">
                                            {survey.vpReward}
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-700 dark:text-slate-300">
                                            {survey.responseCount}
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-700 dark:text-slate-300">
                                            {survey.viewCount}
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-700 dark:text-slate-300">
                                            {survey.completionRate.toFixed(1)}%
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600 dark:text-slate-400">
                                            {new Date(
                                                survey.createdAt,
                                            ).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-right text-sm whitespace-nowrap">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/longhouse/surveys/${survey.id}/edit`,
                                                        )
                                                    }
                                                    disabled={
                                                        survey.hasResponses
                                                    }
                                                    className={`rounded p-1.5 transition ${
                                                        survey.hasResponses
                                                            ? "cursor-not-allowed text-slate-300 dark:text-slate-600"
                                                            : "text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                                                    }`}
                                                    title={
                                                        survey.hasResponses
                                                            ? "Cannot edit - has responses"
                                                            : "Edit"
                                                    }
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/longhouse/surveys/${survey.id}/analytics`,
                                                        )
                                                    }
                                                    className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                                                    title="Analytics"
                                                >
                                                    <BarChart2 className="h-4 w-4" />
                                                </button>
                                                {survey.status === "draft" && (
                                                    <button
                                                        onClick={() =>
                                                            activateSurvey({
                                                                variables: {
                                                                    id: survey.id,
                                                                },
                                                            })
                                                        }
                                                        className="rounded p-1.5 text-green-600 transition hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30"
                                                        title="Activate"
                                                    >
                                                        ▶
                                                    </button>
                                                )}
                                                {survey.status === "active" && (
                                                    <button
                                                        onClick={() =>
                                                            closeSurvey({
                                                                variables: {
                                                                    id: survey.id,
                                                                },
                                                            })
                                                        }
                                                        className="rounded p-1.5 text-orange-600 transition hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/30"
                                                        title="Close"
                                                    >
                                                        ■
                                                    </button>
                                                )}
                                                {survey.responseCount === 0 && (
                                                    <button
                                                        onClick={() => {
                                                            setDeletingSurveyId(
                                                                survey.id,
                                                            );
                                                        }}
                                                        className="rounded p-1.5 text-red-600 transition hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete confirmation */}
            {deletingSurveyId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="w-full max-w-md rounded-2xl border border-slate-300 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
                            Delete Survey?
                        </h3>
                        <p className="mb-6 text-slate-600 dark:text-slate-400">
                            This action cannot be undone. The survey and all its
                            questions will be permanently deleted.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeletingSurveyId(null)}
                                className="rounded-lg border border-slate-300 bg-white px-6 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() =>
                                    deleteSurvey({
                                        variables: { id: deletingSurveyId },
                                    })
                                }
                                className="rounded-lg bg-red-600 px-6 py-2 font-medium text-white transition-colors hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
